import { useCallback, useState } from 'react';

import { dbg } from '@/lib/logto';
import { attendiSyncAlTocco, giudizioSyncAlTocco, type EsitoSyncAlTocco } from '@/lib/limulo/registration';
import { usePostLimuloProfileSyncMutation } from '@/services/limuloApi';

/**
 * **Il collegamento del profilo al TOCCO di Invia e di Accetta** (kb#388, tma-orcr-43). Il sync del provider lo lancia la
 * riga o il foglio all'apertura (`useSyncProfiloLimulo`), senza aspettarlo; qui si decide cosa fa il TOCCO quando il
 * profilo c'e' gia'. Regola in `lib/limulo/registration.ts` (`giudizioSyncAlTocco`, `attendiSyncAlTocco`):
 *  · nessun sync da aspettare ⇒ `procedi` gira SUBITO, nello stesso tick (come prima: niente microtask in mezzo);
 *  · un sync in volo, o l'ultimo caduto per un guasto ⇒ si aspetta (UNO solo, col tetto di `SYNC_TETTO_MS`) e `collegando`
 *    e' vero: la superficie dice «Sto collegando il tuo profilo…» invece di un pulsante morto;
 *  · l'ultimo e' un 403 ⇒ `procedi` riceve `identita` senza rete: ritentare non serve.
 * `procedi` riceve sempre un verdetto e decide lui: `ok` ⇒ manda la sfida; altro ⇒ non la manda e dice la frase.
 *
 * ⚠️ `collega` e' STABILE (dipende solo dalla mutation di RTK): sta nelle dipendenze di `onAccetta`/`invia`, e una
 * funzione nuova a ogni render rimetterebbe in ciclo il provider (l'anello OOM descritto in `useRispostaSfida`).
 * ⚠️ `collegando` e' uno stato che serve SOLO a mostrare: non entra nelle dipendenze di nessun callback.
 */
export function useCollegamentoProfilo() {
  const [syncProfile] = usePostLimuloProfileSyncMutation();
  const [collegando, setCollegando] = useState(false);

  const collega = useCallback(
    (profileId: string | null, procedi: (esito: EsitoSyncAlTocco) => void): void => {
      const giudizio = giudizioSyncAlTocco(profileId);
      if (giudizio !== 'attendi') {
        procedi(giudizio);
        return;
      }
      setCollegando(true);
      void attendiSyncAlTocco({ profileId, sync: (id) => syncProfile(id), log: dbg })
        // Un'attesa che RIGETTA (non dovrebbe: `sincronizzaProfilo` non lancia) vale «non risponde»: `procedi` la riceve e rilascia le
        // sue guardie (la sfida «in volo», `inVolo` dell'invio) come per ogni altro verdetto che non manda. Senza, il tocco restava
        // preso per sempre. Se `procedi` stesso lancia, si scrive nel registro `dbg` (e `collegando` si spegne comunque).
        .catch((e: unknown): EsitoSyncAlTocco => {
          dbg(`[limulo] attesa del sync in errore: ${e instanceof Error ? e.message : String(e)}`);
          return { kind: 'non-risponde' };
        })
        .then(procedi)
        .catch((e: unknown) => dbg(`[limulo] tocco dopo il sync in errore: ${e instanceof Error ? e.message : String(e)}`))
        .finally(() => setCollegando(false));
    },
    [syncProfile],
  );

  return { collega, collegando };
}
